import { z } from 'zod/v4';

export const BulkCreateQuestionnaireRequestsParamsSchema = z.object({
  id: z.string().min(1),
});

export type BulkCreateQuestionnaireRequestsParams = z.infer<
  typeof BulkCreateQuestionnaireRequestsParamsSchema
>;

/**
 * One company being invited to answer the questionnaire.
 *
 * `onBehalfOfCompanyId` is who the answers are *about*; `forCompanyId` is who
 * actually fills them in. They are the same company unless the inviter delegated
 * answering to a third party (an agent filling in on a factory's behalf).
 *
 * Note what is **not** here: `questionnaireId`, which the legacy items carried on
 * every element. It now lives in the path — see the body schema below.
 */
export const BulkCreateQuestionnaireRequestItemSchema = z.object({
  forCompanyId: z.string().min(1),
  onBehalfOfCompanyId: z.string().min(1),
});

export type BulkCreateQuestionnaireRequestItem = z.infer<
  typeof BulkCreateQuestionnaireRequestItemSchema
>;

/**
 * Body of `POST /api/questionnaires/:id/requests`, ported from the legacy
 * `bulkCreate.body` JSON Schema.
 *
 * Two deliberate divergences from that schema:
 *
 * 1. The legacy route took a **bare JSON array**. This wraps it in a named field,
 *    for the reasons `questionnaire-archive.ts` gives: the payload is
 *    self-describing in the generated OpenAPI reference, validation paths read
 *    `body/requests/0` rather than `body/0`, and further fields can be added
 *    without a breaking change.
 * 2. `questionnaireId` moved from every item into the **path**. The legacy service
 *    only ever ownership-checked `requests[0].questionnaireId` and used that same
 *    id for the activation UPDATE and the statistics event, so a batch spanning
 *    two questionnaires was validated against one of them and mis-reported to the
 *    other. Every caller in both frontends already sent one repeated id, so
 *    hoisting it to the path removes the hazard without removing a capability.
 *    It also matches the sibling `GET /api/questionnaires/:id/requests`.
 *
 * Constraints otherwise map 1:1: `minItems: 1` → `.min(1)`, and the item fields
 * stay all-required. Deliberately **uncapped**, as the legacy route was — a
 * `.max()` would newly reject payloads that work today.
 *
 * The ids gain `.min(1)`, which the legacy `{type: 'string'}` items lacked. That
 * is a tightening no real caller hits (an empty company id could never resolve a
 * profile, so legacy answered 404 `company_not_found` where this answers 400).
 *
 * Duplicates within one batch are deliberately NOT rejected here: the service
 * checks each item against the stored rows in order, so `[a, a]` passes the first
 * check and fails the second with `request_already_exist` — exactly as legacy did.
 */
export const BulkCreateQuestionnaireRequestsBodySchema = z.object({
  requests: z.array(BulkCreateQuestionnaireRequestItemSchema).min(1),
});

export type BulkCreateQuestionnaireRequestsBody = z.infer<
  typeof BulkCreateQuestionnaireRequestsBodySchema
>;
